import {useMemo} from "react";
import {useSetting} from "../../contexts/setting";
import cn from "classnames";
import TemplateControls from "../../layout/TemplateControls";
import SortableList from "../../layout/SortableList";
import HighlightShortcuts from "../../layout/HighlightShortcuts";
import AllProductsWhatsNew from "./WhatsNewPreview/AllProductsWhatsNew";
import {useRouteTemplates} from "hooks/useRouteTemplates";
import {useCurrentSet} from "hooks/useCurrentSet";
import {useReleaseSocialMediaFormatting} from "hooks/useReleaseSocialMediaFormatting";
import {KED_PRODUCTS_SET} from "data/products/index";
import {isAirBanner} from "./AirBanner";
const WHATS_NEW_SHORTCUTS = [
  {combination: "⌘ + B", description: "toggle bold"},
  {
    combination: "⌘ + ⌥ + B",
    description: "toggle bold + brand color",
  },
];

const NO_TEMPLATES = Object.freeze([]);
const MODUL_SHORTCUTS = [{combination: "⌘ + ⌥ + A", description: "toggle JetBrains Modul"}];

const Releases = () => {
  const {
    visible,
    product,
    currentVideoPopup,
    channel,
    bases,
  } = useSetting();
  // One resolver for the active set, shared with the toolbar: it also clamps a
  // `?set=` the current product does not offer.
  const selectedSet = useCurrentSet();
  const enableModulHotkey = useReleaseSocialMediaFormatting();

  const isAllProducts = product.value === "ALL" && selectedSet === "whats-new";

  // The set's banners, and the banners an admin added from other sets, without
  // the removed ones (libs/bannerComposition.ts). The ALL view draws its own.
  const routeTemplates = useRouteTemplates("releases");
  const filteredTemplates = isAllProducts ? NO_TEMPLATES : routeTemplates;
  const bannerIds = useMemo(
    () => filteredTemplates.map((template) => template.id),
    [filteredTemplates]
  );
  const hasOnlyOneBanner = bannerIds.length === 1;

  const isWhatsNewSet = selectedSet === "whats-new";
  const showKedShortcuts = KED_PRODUCTS_SET.has(product.value);
  // The bold / bold + brand colour hotkeys live on the legacy What's New
  // banner's feature list (pages/Releases/WhatsNewPreview). JetBrains Air
  // replaces that banner with its own skin, which wires neither, so the hint
  // would name two shortcuts that do nothing. `isAirBanner` is the same
  // predicate that swaps the banner, so the two cannot drift apart.
  const isAir = isAirBanner(product.value);

  // "All Products" mode for whats-new set
  if (isAllProducts) {
    return (
      <>
        <HighlightShortcuts items={WHATS_NEW_SHORTCUTS} />
        <AllProductsWhatsNew />
      </>
    );
  }

  return (
    <>
      {isWhatsNewSet && !isAir && <HighlightShortcuts items={WHATS_NEW_SHORTCUTS} />}
      {!isWhatsNewSet && (showKedShortcuts || enableModulHotkey) && (
        <HighlightShortcuts
          items={showKedShortcuts ? undefined : []}
          additionalItems={enableModulHotkey ? MODUL_SHORTCUTS : []}
        />
      )}
      <SortableList
        items={filteredTemplates}
        useDragHandle={true}
        getItemClassName={(item) =>
          cn("template-wrap", {
            hidden: !hasOnlyOneBanner && !visible.some((x) => x === item.id),
            popup: currentVideoPopup === item.id,
          })
        }
        // `--base-color` is a scene scalar on `.templates-inner` (App.js); a
        // banner that set its base apart on its look redeclares it here, so the
        // fill under its artwork is its own and every other banner keeps the
        // shared one. Same element the renderer screenshots, so an export and
        // the editor cannot disagree.
        getItemStyle={(item) =>
          bases?.[item.id] ? {"--base-color": `#${bases[item.id]}`} : undefined
        }
        renderItem={(item, {listeners, attributes} = {}) => (
          <>
            <div
              className="template-annotation"
              {...(listeners || {})}
              {...(attributes || {})}
              style={{cursor: "grab"}}
            >
              <span
                className="label"
                data-tooltip={item.tooltip || null}
              >{`${item.label} (${item.dimensions}${item.suffix || ""})`}</span>
              <TemplateControls
                id={item.id}
                templateChannel="releases"
                hasOnlyOneBanner={hasOnlyOneBanner}
                bannerIds={bannerIds}
              />
            </div>
            {item.getComponent()}
          </>
        )}
      />
    </>
  );
};

export default Releases;
